import React from 'react';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Component that conditionally renders children based on user permissions
 */
const PermissionGuard = ({ 
  permission, 
  permissions, 
  requireAll = false,
  children, 
  fallback = null,
  showFallback = true 
}) => {
  const { hasPermission } = usePermissions();

  // Check single permission
  if (permission) {
    const hasAccess = hasPermission(permission);
    return hasAccess ? children : (showFallback ? fallback : null);
  }

  // Check multiple permissions
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll 
      ? permissions.every(perm => hasPermission(perm))
      : permissions.some(perm => hasPermission(perm));
    
    return hasAccess ? children : (showFallback ? fallback : null);
  }

  // No permissions specified, render children
  return children;
};

/**
 * Higher-order component for permission-based rendering
 */
export const withPermission = (permission, fallback = null) => (Component) => {
  return (props) => (
    <PermissionGuard permission={permission} fallback={fallback}>
      <Component {...props} />
    </PermissionGuard>
  );
};

export default PermissionGuard;